import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RIGHT_DRAWER } from '@flexo/reducers';
import { useLocation } from 'react-router-dom';
import styles from './right-drawer-content.module.scss';
import {
  formatWeek,
  formatDay,
  formatMonth,
  formatYear,
  checkItIsToday,
} from '../../../../../organisms/src/lib/hive-calendar/helpers';
import { formatToMaxFourDigits } from '@flexo/general';
import { DrawerCloseButton } from '../drawer-close-button';
import parse from 'html-react-parser';
import React from 'react';

interface DrawerConfig {
  titleKey?: string;
  descriptionKey?: string;
}

interface CommunityCo2DrawerContentProps {
  drawerConfig?: DrawerConfig;
}

/**
 * FLEXO-6066 detail drawer: Community CO2.
 * Mirrors CommunityTotalEstimatedBenefitsDrawerContent: reads the already-loaded
 * energy KPI (`co2_offset_kgs`, `co2_offset_trees`) for the selected entity. The
 * content is entity-aware (community / member / site) so it can be reused.
 */
export const CommunityCo2DrawerContent: React.FC<
  CommunityCo2DrawerContentProps
> = ({ drawerConfig }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const metadata = useSelector((state: any) => state.metadata);
  const kpi = useSelector((state: any) => state.kpi);
  const calendar = useSelector((state: any) => state.calendar);
  const location = useLocation();

  const pathSegment = location.pathname.split('/')[1];
  const {
    hiveCalendar: { selectedDate, selectedTimeSet },
  } = calendar;

  let entityType = 'site';
  if (pathSegment?.includes('member')) {
    entityType = 'member';
  } else if (pathSegment?.includes('community')) {
    entityType = 'community';
  }

  const config: any = {
    community: { getName: (e: any) => e?.communityName, entity: 'Community' },
    member: {
      getName: (e: any) =>
        `${e?.memberFirstName || ''} ${e?.memberLastName || ''}`.trim(),
      entity: 'Member',
    },
    // FLEXO-6133 #6d: friendly site name first — end-users don't know their EIC.
    site: { getName: (e: any) => e?.siteName || e?.pod, entity: 'Site' },
  }[entityType];

  // CO2 KPIs (co2_offset_kgs / co2_offset_trees) live in the Ancillary KPI set
  // (the pollution card uses kpiContext: 'ancillary').
  const overviewKpiApiData =
    (kpi[`Ancillary${config.entity}`]?.['1day']?.[0]?.kpi as any) || null;
  const entity = metadata?.selectedEntity || {};

  function getKpi(kpiName: string) {
    const k = (overviewKpiApiData || []).find((x: any) => x.name === kpiName);
    if (!k) return '-.-';
    return `${formatToMaxFourDigits(k.value)} ${k.unit || ''}`.trim();
  }

  function closeDrawer() {
    dispatch(RIGHT_DRAWER.HideDrawer());
  }

  const getDate = () => {
    let result = '';
    switch (selectedTimeSet) {
      case 'day':
        if (checkItIsToday(selectedDate)) result = t('widgets.calendar.today');
        else result = formatDay(selectedDate);
        break;
      case 'week':
        result = selectedDate[0] ? `${formatWeek(selectedDate)}` : 'Select Week';
        break;
      case 'month':
        result = formatMonth(selectedDate);
        break;
      case 'year':
        result = formatYear(selectedDate);
        break;
      default:
        result = formatDay(selectedDate);
        break;
    }
    return result;
  };

  return (
    <div className={styles.RightDrawerContent__Wrapper}>
      <div className={styles.RightDrawerContent__Title}>
        <p className={styles.RightDrawerContent__Paragraph}>
          {parse(t(drawerConfig?.titleKey || 'widgets.pollutionCard.details.title'))}
        </p>
        <DrawerCloseButton onClose={closeDrawer} ariaLabel={t('common.close', { defaultValue: 'Close' })} />
      </div>
      <div className={styles.RightDrawerContent__Subtitle}>
        <p className={styles.RightDrawerContent__Paragraph}>
          {config.getName(entity)} <label>{getDate()}</label>
        </p>
      </div>
      <div className={styles.RightDrawerContent__Body}>
        <p className={styles.RightDrawerContent__Body__Value}>
          {getKpi('co2_offset_kgs')}
        </p>
        <p className={styles.RightDrawerContent__Body__Description}>
          {parse(t('widgets.pollutionCard.details.label'))}
        </p>
        <p className={styles.RightDrawerContent__Body__List__Title}>
          {parse(t('widgets.pollutionCard.details.value_1'))}
        </p>
        <br />
        <p className={styles.RightDrawerContent__Body__Description}>
          {parse(t('widgets.pollutionCard.details.label_2_1'))}
          {getKpi('co2_offset_trees')}{' '}
          {parse(t('widgets.pollutionCard.details.label_2_2'))}
        </p>
        <p className={styles.RightDrawerContent__Body__List__Title}>
          {parse(t('widgets.pollutionCard.details.value_2'))}
        </p>
      </div>
    </div>
  );
};

export default CommunityCo2DrawerContent;
